import React, { useState, useRef, useEffect } from 'react';
import { PostIt, PostItType } from '../types';
import { Target } from 'lucide-react';
import { useProjectStore } from '../store/useProjectStore';

interface PostItProps {
  data: PostIt;
  scale: number;
  isBrainstormMode: boolean;
  isActive: boolean;
  isSnapping?: boolean;
  isLinking?: boolean; // Post-it selecionado para linking (click-to-link mode)
  isKaizenMode?: boolean;
  parentCard?: { id: string; x: number; y: number; width: number; height: number; minimizedIndex?: number; totalMinimized?: number } | null;
  minimizedPosition?: { x: number; y: number } | null;
  onStartDrag: (id: string, offsetX: number, offsetY: number) => void;
  onDrag?: (clientX: number, clientY: number) => void;
  onEndDrag?: () => void;
  onChangeText: (id: string, text: string) => void;
  onToggleMinimize?: (id: string) => void;
  onClick?: (id: string, e?: React.MouseEvent) => void;
  onContextMenu?: (id: string, x: number, y: number) => void;
  onOpenImpactPanel?: (postItId: string) => void;
  selected?: boolean;
  forceEdit?: boolean;
}

// Dimensoes do post-it (mesmo tamanho visual dos card-attached)
const POST_IT_WIDTH = 80;
const POST_IT_HEIGHT = 72;

// Tailwind-compatible type styles (mesmas cores do ActivityNode)
const postItTypeStyles: Record<string, { bg: string; borderColor: string }> = {
  idea:    { bg: '#fcd34d', borderColor: '#fcd34d' },   // amber-300
  problem: { bg: '#fca5a5', borderColor: '#fca5a5' },   // red-300
  action:  { bg: '#86efac', borderColor: '#86efac' },   // emerald-300
  data:    { bg: '#93c5fd', borderColor: '#93c5fd' },   // blue-300
  risk:    { bg: '#c4b5fd', borderColor: '#c4b5fd' },   // purple-300
};

export const PostItCard: React.FC<PostItProps> = ({
  data,
  scale,
  isBrainstormMode,
  isActive,
  isSnapping = false,
  isLinking = false,
  isKaizenMode = false,
  parentCard,
  minimizedPosition,
  onStartDrag,
  onDrag,
  onEndDrag,
  onChangeText,
  onToggleMinimize,
  onClick,
  onContextMenu,
  onOpenImpactPanel,
  forceEdit = false,
  selected = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [suppressHover, setSuppressHover] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const dragStartPos = useRef<{ x: number; y: number } | null>(null);

  // Forcar modo edicao vindo de fora
  useEffect(() => {
    if (forceEdit) {
      setIsEditing(true);
    }
  }, [forceEdit]);

  // Sair do modo edicao com UM clique fora (captura antes do preventDefault do canvas)
  useEffect(() => {
    if (!isEditing) return;

    const handleOutsideMouseDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        textareaRef.current?.blur();
        setIsEditing(false);
        setIsHovering(false);
        setSuppressHover(true);
      }
    };

    document.addEventListener('pointerdown', handleOutsideMouseDown, true); // fase captura
    return () => document.removeEventListener('pointerdown', handleOutsideMouseDown, true);
  }, [isEditing]);

  // Calcular posicao real considerando ancoragem
  const getActualPosition = () => {
    if (data.fixedToNodeId && parentCard) {
      return {
        x: parentCard.x + data.relativeOffset.x,
        y: parentCard.y + data.relativeOffset.y,
      };
    }
    return { x: data.x, y: data.y };
  };

  const actualPos = getActualPosition();

  // Focar e selecionar texto ao entrar em modo edicao
  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus({ preventScroll: true });
      const val = textareaRef.current.value;
      if (val.length < 3 || val === 'Nova Acao' || val === 'Nova Atividade') {
        textareaRef.current.select();
      } else {
        textareaRef.current.setSelectionRange(val.length, val.length);
      }
    }
  }, [isEditing]);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (isEditing) {
      e.stopPropagation();
      return;
    }
    e.stopPropagation();

    dragStartPos.current = { x: e.clientX, y: e.clientY };
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const offsetX = e.clientX - rect.left;
    const offsetY = e.clientY - rect.top;
    onStartDrag(data.id, offsetX, offsetY);
  };

  const handleMouseUp = (e: React.MouseEvent) => {
    if (isEditing) return;
    if (dragStartPos.current) {
      const dx = Math.abs(e.clientX - dragStartPos.current.x);
      const dy = Math.abs(e.clientY - dragStartPos.current.y);
      if (dx < 3 && dy < 3) {
        e.stopPropagation();
        if (onClick) {
          onClick(data.id, e);
        }
        setIsEditing(true);
      }
      dragStartPos.current = null;
    }
  };

  const handleDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditing(true);
    requestAnimationFrame(() => {
      textareaRef.current?.focus();
      textareaRef.current?.select();
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      setIsEditing(false);
      setIsHovering(false);
      setSuppressHover(true);
    }
    if (e.key === 'Escape') {
      setIsEditing(false);
      setIsHovering(false);
      setSuppressHover(true);
      textareaRef.current?.blur();
    }
  };

  const handleBlur = () => {
    setIsEditing(false);
    setIsHovering(false);
    setSuppressHover(true); // impede zoom por hover ate o mouse sair fisicamente
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChangeText(data.id, e.target.value);
  };

  // Handler global para movimento do mouse durante arraste
  useEffect(() => {
    if (!isActive || isEditing) return;

    const handleGlobalMouseMove = (e: MouseEvent) => {
      if (onDrag) {
        onDrag(e.clientX, e.clientY);
      }
    };

    const handleGlobalMouseUp = () => {
      if (onEndDrag) {
        onEndDrag();
      }
    };

    document.addEventListener('mousemove', handleGlobalMouseMove);
    document.addEventListener('mouseup', handleGlobalMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [isActive, isEditing, onDrag, onEndDrag]);

  const typeStyle = postItTypeStyles[data.type] || { bg: data.color || '#e5e7eb', borderColor: data.color || '#d1d5db' };
  const textContent = data.text || '';
  const fontSize = Math.max(5.5, 10 - Math.floor(textContent.length / 15) * 0.4);

  // isGrabbed = ativamente sendo arrastado OU selecionado para linking (click-to-link)
  const isGrabbed = isActive || isLinking;

  // Hover effect: mesma escala do ActivityNode (2x com leve rotacao)
  const getTransform = () => {
    if (isGrabbed) return 'scale(1.3) rotate(-2deg)';
    if (isEditing) return 'scale(2.2)';
    if (isHovering && !suppressHover) return 'scale(2.0) rotate(-1deg)';
    return 'scale(1)';
  };

  const getBoxShadow = () => {
    if (isGrabbed) {
      return '0 0 0 2px rgba(37, 99, 235, 0.7), 0 16px 32px -4px rgba(0,0,0,0.3), 0 8px 16px -4px rgba(0,0,0,0.2)';
    }
    if (isEditing) {
      return '0 0 0 2px rgba(37, 99, 235, 0.85), 0 12px 28px -4px rgba(0,0,0,0.25)';
    }
    if (isHovering) {
      return '0 0 0 0.5px rgba(0,0,0,0.45), 0 10px 25px -5px rgba(0,0,0,0.2), 0 8px 10px -6px rgba(0,0,0,0.1)';
    }
    if (isSnapping) {
      return '0 0 0 2px #2563eb, 0 4px 8px rgba(37, 99, 235, 0.4)';
    }
    if (selected) {
      return '0 0 0 2px rgba(37, 99, 235, 0.8), 0 0 0 0.5px rgba(0,0,0,0.45)';
    }
    return '0 0 0 0.5px rgba(0,0,0,0.45)';
  };

  // Gerar cursor SVG inline: mão fechada segurando post-it colorido (para arrastar/linking)
  const getGrabbingCursor = () => {
    const bg  = typeStyle.bg;
    const bdr = typeStyle.borderColor;
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32"><polygon points="2,2 20,2 20,7 24,7 24,22 2,22" fill="${bg}" stroke="${bdr}" stroke-width="1.2"/><polygon points="20,2 24,7 20,7" fill="${bdr}" opacity="0.6"/><line x1="5" y1="11" x2="21" y2="11" stroke="${bdr}" stroke-width="0.9" opacity="0.5"/><line x1="5" y1="15" x2="21" y2="15" stroke="${bdr}" stroke-width="0.9" opacity="0.5"/><rect x="5" y="22" width="4" height="7" rx="2" fill="#d4956a"/><rect x="10" y="21" width="4" height="9" rx="2" fill="#d4956a"/><rect x="15" y="21" width="4" height="9" rx="2" fill="#d4956a"/><rect x="20" y="23" width="3" height="7" rx="1.5" fill="#d4956a"/><rect x="5" y="26" width="19" height="4" rx="2" fill="#c8845a"/></svg>`;
    return `url("data:image/svg+xml,${encodeURIComponent(svg)}") 4 1, grabbing`;
  };

  return (
    <div
      ref={containerRef}
      data-post-it="true"
      data-post-it-id={data.id}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onDoubleClick={handleDoubleClick}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => { setIsHovering(false); setSuppressHover(false); }}
      onContextMenu={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (onContextMenu && data.id) {
          onContextMenu(data.id, e.clientX, e.clientY);
        }
      }}
      style={{
        position: 'absolute',
        left: actualPos.x,
        top: actualPos.y,
        width: `${POST_IT_WIDTH}px`,
        minHeight: `${POST_IT_HEIGHT}px`,
        backgroundColor: typeStyle.bg,
        cursor: isEditing ? 'text' : isGrabbed ? getGrabbingCursor() : 'grab',
        zIndex: isGrabbed ? 9999 : isEditing ? 5000 : isHovering ? 2000 : 90,
        padding: '1px',
        overflow: 'visible',
        boxShadow: getBoxShadow(),
        transform: getTransform(),
        transformOrigin: 'top center',
        opacity: isGrabbed ? 0.92 : 1,
        transition: isGrabbed
          ? 'box-shadow 0.15s ease, transform 0.15s ease, opacity 0.15s ease'
          : 'transform 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275), z-index 0.1s ease, box-shadow 0.2s ease',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Conteudo: texto editavel ou visualizacao */}
      {isEditing ? (
        <textarea
          ref={textareaRef}
          value={data.text}
          onChange={handleTextChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          style={{
            flex: 1,
            width: '100%',
            minHeight: '60px',
            resize: 'none',
            border: 'none',
            outline: 'none',
            boxShadow: 'none',
            background: 'transparent',
            backgroundColor: 'transparent',
            appearance: 'none',
            WebkitAppearance: 'none',
            display: 'block',
            textAlign: 'center',
            marginTop: 'auto',
            marginBottom: 'auto',
            fontSize: `${Math.max(6, 10 - Math.floor(textContent.length / 18) * 0.4)}px`,
            lineHeight: '1.15',
            color: '#111827',
            fontWeight: 500,
            padding: '2px 2px',
            fontFamily: 'inherit',
          }}
        />
      ) : (
        <div style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0 2px',
          overflow: 'hidden',
          minHeight: `${POST_IT_HEIGHT - (isKaizenMode ? 14 : 0)}px`,
        }}>
          <span style={{
            color: '#111827',
            textAlign: 'center',
            lineHeight: '1.15',
            wordBreak: 'break-word',
            whiteSpace: 'pre-wrap',
            hyphens: 'auto' as const,
            fontWeight: 500,
            display: 'block',
            width: '100%',
            fontSize: `${fontSize}px`,
          }}>
            {textContent || (
              <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>Clique para digitar...</span>
            )}
          </span>
        </div>
      )}

      {/* Footer: Botao de Impacto (modo Kaizen) */}
      {isKaizenMode && (
        <div
          style={{
            width: '100%',
            height: '14px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderTop: '1px solid rgba(0,0,0,0.1)',
            backgroundColor: 'rgba(139, 92, 246, 0.2)',
            cursor: 'pointer',
            flexShrink: 0,
          }}
          onClick={(e) => {
            e.stopPropagation();
            if (onOpenImpactPanel) {
              onOpenImpactPanel(data.id);
            }
          }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div style={{
            fontSize: 8,
            fontWeight: 700,
            color: '#7c3aed',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}>
            <Target size={8} strokeWidth={2.5} />
            <span>Impactos</span>
          </div>
        </div>
      )}

      {/* Dobra no canto inferior direito */}
      <div style={{
        position: 'absolute',
        bottom: 0,
        right: 0,
        width: '12px',
        height: '12px',
        background: 'rgba(0,0,0,0.12)',
        clipPath: 'polygon(100% 0, 100% 100%, 0 100%)',
        borderBottomRightRadius: '3px',
        pointerEvents: 'none',
      }} />
    </div>
  );
};
